import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR } from '../common/data';

/**
 * O que exige ação no Administrativo hoje.
 *
 * Existe porque `alerta_dias` era configurável desde sempre e o aviso nunca
 * saía do banco: a tela listava alvarás por data de vencimento e cabia ao
 * gestor reparar sozinho que um deles já tinha passado. Aqui o que venceu vem
 * primeiro, em vermelho, com o número de dias escrito por extenso.
 *
 * A situação e o texto do prazo vêm calculados do backend
 * (`administrativo-calculos.ts`) — a tela não recalcula data, justamente para
 * não reintroduzir o fuso do PR #82 em uma segunda implementação.
 */

interface LicencaAtencao {
  id: number;
  orgao: string;
  documento: string;
  vencimento: string | null;
  situacao: 'vencido' | 'vence_em_breve' | 'em_dia' | 'sem_validade';
  diasRestantes: number | null;
  prazoTexto: string;
}

interface ResumoAdministrativo {
  hoje: string;
  financeiro: number;
  inventario: number;
  relatorios: number;
  licencas: { total: number; vencidas: number; vencendo: number; atencao: LicencaAtencao[] };
  ultimaVistoria: {
    id: number;
    data: string;
    resumo: {
      total: number; conformidadePct: number | null;
      pendencias: number; pendenciasVencidas: number; naoVerificados: number; concluida: boolean;
    };
  } | null;
}

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20,
};

const CORES = { vencido: '#c62828', vence_em_breve: '#e65100', ok: '#2e7d32', neutro: '#616161' };

function Numero({ valor, label, cor }: { valor: React.ReactNode; label: string; cor: string }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: cor, lineHeight: 1.1 }}>{valor}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    </div>
  );
}

export default function PainelAlertas({ recarregarEm }: { recarregarEm?: number }) {
  const [resumo, setResumo] = useState<ResumoAdministrativo | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get<ResumoAdministrativo>('/administrativo/resumo');
      setResumo(data);
      setErro('');
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar o painel do Administrativo.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar, recarregarEm]);

  if (carregando && !resumo) return <div style={card}>Carregando o painel…</div>;
  if (erro) return <div style={{ ...card, color: CORES.vencido }}>{erro}</div>;
  if (!resumo) return null;

  const { licencas, ultimaVistoria } = resumo;
  const tudoEmDia = licencas.vencidas === 0 && licencas.vencendo === 0;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>🏢 Situação da unidade</h3>
        <button type="button" onClick={carregar} disabled={carregando}>
          {carregando ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
        <Numero
          valor={licencas.vencidas}
          label={licencas.vencidas === 1 ? 'licença vencida' : 'licenças vencidas'}
          cor={licencas.vencidas > 0 ? CORES.vencido : CORES.neutro}
        />
        <Numero
          valor={licencas.vencendo}
          label="vencem em breve"
          cor={licencas.vencendo > 0 ? CORES.vence_em_breve : CORES.neutro}
        />
        <Numero valor={licencas.total} label="licenças cadastradas" cor={CORES.neutro} />
        {ultimaVistoria && (
          <Numero
            valor={ultimaVistoria.resumo.conformidadePct === null ? '—' : `${ultimaVistoria.resumo.conformidadePct}%`}
            label="conformidade na última vistoria"
            cor={CORES.neutro}
          />
        )}
      </div>

      {/* Lista só o que pede ação. Alvará em dia não precisa ser lido todo dia. */}
      {tudoEmDia ? (
        <p style={{ color: CORES.ok, margin: 0 }}>
          ✅ Nenhuma licença vencida ou perto de vencer.
          {licencas.total === 0 && ' (Nenhuma licença cadastrada ainda.)'}
        </p>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr><th>Documento</th><th>Órgão</th><th>Vencimento</th><th>Situação</th></tr>
          </thead>
          <tbody>
            {licencas.atencao.map((l) => (
              <tr key={l.id}>
                <td>{l.documento}</td>
                <td>{l.orgao}</td>
                <td>{formatarDataBR(l.vencimento)}</td>
                <td style={{ color: l.situacao === 'vencido' ? CORES.vencido : CORES.vence_em_breve, fontWeight: 600 }}>
                  {l.situacao === 'vencido' ? '🔴' : '🟠'} {l.prazoTexto}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {ultimaVistoria && (
        <p style={{ marginTop: 14, marginBottom: 0, fontSize: 13, color: '#555' }}>
          Última vistoria em <strong>{formatarDataBR(ultimaVistoria.data)}</strong>
          {' — '}
          {ultimaVistoria.resumo.pendenciasVencidas > 0 && (
            <span style={{ color: CORES.vencido, fontWeight: 600 }}>
              {ultimaVistoria.resumo.pendenciasVencidas}{' '}
              {ultimaVistoria.resumo.pendenciasVencidas === 1
                ? 'correção com prazo vencido'
                : 'correções com prazo vencido'}
              {'. '}
            </span>
          )}
          {ultimaVistoria.resumo.pendencias > 0
            ? `${ultimaVistoria.resumo.pendencias} ${ultimaVistoria.resumo.pendencias === 1 ? 'pendência em aberto' : 'pendências em aberto'}.`
            : 'sem pendências em aberto.'}
          {!ultimaVistoria.resumo.concluida && ultimaVistoria.resumo.naoVerificados > 0 && (
            <> {ultimaVistoria.resumo.naoVerificados} {ultimaVistoria.resumo.naoVerificados === 1 ? 'item ainda não verificado' : 'itens ainda não verificados'}.</>
          )}
        </p>
      )}
    </div>
  );
}
